import { Hint, Panel, Slider, Toggle } from './ui/Controls';
import { store, useStore } from '@/state/store';

/** Photo treatment and overlay strength for the active design. */
export function ControlsPanel() {
  const spec = useStore((s) => s.variants.find((v) => v.id === s.activeId) ?? null);

  if (!spec) {
    return (
      <Panel title="Image & overlay">
        <p className="text-[11px] text-ink-500">Generate a design to adjust the photo.</p>
      </Panel>
    );
  }

  const setImage = (patch: Partial<typeof spec.image>, key: string) => {
    store.updateActive((current) => ({ ...current, image: { ...current.image, ...patch } }), key);
  };

  const overlays = spec.overlays;
  const primaryOverlay = overlays[0] ?? null;

  const setOverlayOpacity = (value: number) => {
    store.updateActive(
      (current) => ({
        ...current,
        overlays: current.overlays.map((ov, i) =>
          i === 0
            ? ov.kind === 'linear' || ov.kind === 'radial'
              ? {
                  ...ov,
                  // Gradients carry their strength in the stops, so scale those
                  // rather than the layer opacity — otherwise the falloff shape
                  // collapses as you drag.
                  stops: ov.stops.map((s, si) => ({
                    ...s,
                    alpha: si === 0 ? 0 : (s.alpha / Math.max(0.001, maxAlpha(ov.stops))) * value,
                  })),
                }
              : { ...ov, opacity: value }
            : ov,
        ),
      }),
      'overlay-opacity',
    );
  };

  const currentOverlayStrength = primaryOverlay
    ? primaryOverlay.kind === 'linear' || primaryOverlay.kind === 'radial'
      ? maxAlpha(primaryOverlay.stops)
      : primaryOverlay.opacity
    : 0;

  return (
    <Panel title="Image & overlay">
      <div className="space-y-2.5">
        <Slider
          label="Brightness"
          min={0.4}
          max={1.6}
          value={spec.image.brightness}
          onChange={(v) => setImage({ brightness: v }, 'img-brightness')}
          format={(v) => `${(v * 100).toFixed(0)}%`}
        />
        <Slider
          label="Contrast"
          min={0.5}
          max={1.8}
          value={spec.image.contrast}
          onChange={(v) => setImage({ contrast: v }, 'img-contrast')}
          format={(v) => `${(v * 100).toFixed(0)}%`}
        />
        <Slider
          label="Saturation"
          min={0}
          max={1.8}
          value={spec.image.saturation}
          onChange={(v) => setImage({ saturation: v }, 'img-saturation')}
          format={(v) => `${(v * 100).toFixed(0)}%`}
        />
        <Slider
          label="Blur"
          min={0}
          max={0.05}
          step={0.001}
          value={spec.image.blur}
          onChange={(v) => setImage({ blur: v }, 'img-blur')}
          format={(v) => `${(v * 1080).toFixed(0)}px`}
        />
        <Slider
          label="Zoom"
          min={1}
          max={2}
          step={0.01}
          value={spec.image.scale}
          onChange={(v) => setImage({ scale: v }, 'img-scale')}
          format={(v) => `${v.toFixed(2)}×`}
        />
        <Slider
          label="Pan X"
          min={-0.35}
          max={0.35}
          step={0.005}
          value={spec.image.offsetX}
          onChange={(v) => setImage({ offsetX: v }, 'img-x')}
          format={(v) => `${(v * 100).toFixed(0)}%`}
        />
        <Slider
          label="Pan Y"
          min={-0.35}
          max={0.35}
          step={0.005}
          value={spec.image.offsetY}
          onChange={(v) => setImage({ offsetY: v }, 'img-y')}
          format={(v) => `${(v * 100).toFixed(0)}%`}
        />

        <div className="border-t border-white/[0.06] pt-2.5">
          {primaryOverlay ? (
            <>
              <Slider
                label={
                  primaryOverlay.kind === 'panel'
                    ? 'Panel opacity'
                    : primaryOverlay.kind === 'solid'
                      ? 'Wash opacity'
                      : 'Gradient strength'
                }
                min={0}
                max={1}
                value={currentOverlayStrength}
                onChange={setOverlayOpacity}
                format={(v) => `${(v * 100).toFixed(0)}%`}
              />
              {primaryOverlay.kind === 'panel' && (
                <div className="mt-2">
                  <Slider
                    label="Panel blur"
                    min={0}
                    max={0.06}
                    step={0.001}
                    value={primaryOverlay.blur}
                    onChange={(v) =>
                      store.updateActive(
                        (current) => ({
                          ...current,
                          overlays: current.overlays.map((ov, i) =>
                            i === 0 && ov.kind === 'panel' ? { ...ov, blur: v } : ov,
                          ),
                        }),
                        'panel-blur',
                      )
                    }
                    format={(v) => `${(v * 1080).toFixed(0)}px`}
                  />
                </div>
              )}
              <Toggle
                label="Show overlay"
                checked={primaryOverlay.visible}
                onChange={(on) =>
                  store.updateActive((current) => ({
                    ...current,
                    overlays: current.overlays.map((ov, i) =>
                      i === 0 ? { ...ov, visible: on } : ov,
                    ),
                  }))
                }
              />
            </>
          ) : (
            <Hint>
              This composition needed no overlay — the background was already
              calm enough behind the text.
            </Hint>
          )}
        </div>
      </div>
    </Panel>
  );
}

function maxAlpha(stops: { alpha: number }[]): number {
  return stops.reduce((m, s) => Math.max(m, s.alpha), 0);
}
